"use client";

import { useMemo } from "react";
import DateRangePicker from "@/components/search/DateRangePicker";
import type { StationSummary } from "@/hooks/useStationHubData";
import {
  campingRentalDays,
  formatCampingDate,
} from "@/lib/mobility/camping-period";
import { CampingGearGrid, campingPeriodLabel } from "./CampingGearGrid";
import { useHub } from "./hub-context";
import { HUB_GUTTER } from "./hub-ui";
import { CountSortRow, StationHeadCard } from "./StationSheetParts";

/**
 * 캠핑장 시트 — 캠핑장을 누르면(핀·목록 행) 열린다.
 * 머리 카드(바이크 지점 시트와 공용) → 받는 곳·돌려주는 곳 안내(지점 메모) →
 * 날짜 줄(고른 기간 + "날짜 변경") → 장비 수·정렬 → 장비 사진 그리드.
 * 장비 가격은 고른 기간 총액이라 날짜 줄이 그 기준을 알려 준다.
 */
export function CampingStationSheet({ station }: { station: StationSummary }) {
  const { t, locale, data, visibleProducts, checkinDate, checkoutDate, onDateChange } =
    useHub();
  const detail = data.details.get(station.id);
  const products = visibleProducts(station);
  const notes = detail?.notes?.trim() ?? "";
  const rentalDays = campingRentalDays(checkinDate, checkoutDate);
  const entries = useMemo(
    () => products.map((product) => ({ product, stationId: station.id })),
    [products, station.id],
  );

  return (
    <div className="pb-8">
      <div className={HUB_GUTTER}>
        <StationHeadCard station={station} />

        {/* 캠핑장 수령·반납 안내 — 지점 메모 원문(접수처 수령, 반납 시각 등) */}
        {notes ? (
          <div className="mt-3 rounded-xl bg-gray-50 px-3.5 py-3 text-[13px] leading-relaxed whitespace-pre-line text-gray-700 mapShow:text-sm">
            <span className="font-semibold text-gray-900">
              {t("hub.camping.pickupNoteLead")}
            </span>
            {" — "}
            {notes}
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-between gap-3">
          {rentalDays && checkinDate && checkoutDate ? (
            <p
              className="min-w-0 text-[14px] text-gray-500 mapShow:text-[15px]"
              suppressHydrationWarning
            >
              <span className="font-semibold text-gray-900">
                {formatCampingDate(checkinDate, locale)} →{" "}
                {formatCampingDate(checkoutDate, locale)}
              </span>
              {" · "}
              {campingPeriodLabel(t, rentalDays)}
            </p>
          ) : (
            <p className="min-w-0 text-[13px] text-gray-500 mapShow:text-sm">
              {t("hub.camping.pickDates")}
            </p>
          )}
          {onDateChange ? (
            <DateRangePicker
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
              onDateChange={onDateChange}
              trigger={
                <button
                  type="button"
                  className="shrink-0 text-[13px] font-medium whitespace-nowrap text-gray-700 underline underline-offset-2 hover:text-gray-900 mapShow:text-sm"
                >
                  {t("hub.camping.changeDates")}
                </button>
              }
            />
          ) : null}
        </div>
      </div>

      <CountSortRow
        label={t("hub.camping.bikesN", { n: products.length })}
        className="pt-4"
      />

      <CampingGearGrid entries={entries} rentalDays={rentalDays} />
    </div>
  );
}
